import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import type { Run } from '../slices/runsSlice'
import DeleteRunConfirmation from './DeleteRunConfirmation'
import './RunsTable.css'

interface RunsTableProps {
  runs: Run[]
  onDelete: (id: string) => void
}

type SortField = 'name' | 'created_at' | 'agent_name' | 'success_rate'
type SortDir = 'asc' | 'desc'

function RunsTable({ runs, onDelete }: RunsTableProps) {
  const navigate = useNavigate()
  const [sortField, setSortField] = useState<SortField>('created_at')
  const [sortDir, setSortDir] = useState<SortDir>('desc')

  const sortedRuns = useMemo(() => {
    const copy = [...runs]
    copy.sort((a, b) => {
      let cmp = 0
      switch (sortField) {
        case 'name':
          cmp = a.name.localeCompare(b.name)
          break
        case 'created_at':
          cmp = new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
          break
        case 'agent_name':
          cmp = a.agent_name.localeCompare(b.agent_name)
          break
        case 'success_rate':
          cmp = (a.success_rate ?? -1) - (b.success_rate ?? -1)
          break
      }
      return sortDir === 'asc' ? cmp : -cmp
    })
    return copy
  }, [runs, sortField, sortDir])

  function handleSort(field: SortField) {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDir('desc')
    }
  }

  function formatDate(dateStr: string): string {
    const date = new Date(dateStr)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (diffHours < 1) return 'Just now'
    if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`
    if (diffDays === 1) return 'Yesterday'
    return `${diffDays} days ago`
  }

  function formatStats(run: Run): string {
    if (run.success_rate === null && run.avg_cost === null) return 'N/A'
    const rate = run.success_rate !== null ? `${Math.round(run.success_rate)}%` : 'N/A'
    const cost = run.avg_cost !== null ? `$${run.avg_cost.toFixed(2)}` : 'N/A'
    return `${rate} / ${cost}`
  }

  function formatSelections(run: Run): string {
    const parts: string[] = []
    if (run.selected_tags.length > 0) parts.push(...run.selected_tags)
    if (run.selected_failures.length > 0) {
      const f = run.selected_failures
      if (f.length <= 3) {
        parts.push(...f)
      } else {
        parts.push(`${f[0]} ... ${f[f.length - 1]} (${f.length})`)
      }
    }
    return parts.join(', ') || '—'
  }

  function renderSortIcon(field: SortField) {
    if (sortField !== field) return null
    return <span className="runs-table-sort-icon">{sortDir === 'asc' ? '↑' : '↓'}</span>
  }

  function handleRowClick(runId: string) {
    navigate(`/runs/${runId}`)
  }

  if (runs.length === 0) {
    return (
      <div data-testid="runs-table-empty" className="runs-empty">
        No runs yet. Create a new run to get started.
      </div>
    )
  }

  return (
    <div data-testid="runs-table" className="runs-table-wrapper">
      <table className="runs-table">
        <thead>
          <tr>
            <th
              className="runs-table-sortable runs-col-name"
              onClick={() => handleSort('name')}
              data-testid="sort-run-name"
            >
              Run ID/Name{renderSortIcon('name')}
            </th>
            <th
              className="runs-table-sortable runs-col-created"
              onClick={() => handleSort('created_at')}
              data-testid="sort-run-created"
            >
              Created{renderSortIcon('created_at')}
            </th>
            <th
              className="runs-table-sortable runs-col-agent"
              onClick={() => handleSort('agent_name')}
              data-testid="sort-run-agent"
            >
              Agent{renderSortIcon('agent_name')}
            </th>
            <th className="runs-col-selections">Selected Groups/Failures</th>
            <th
              className="runs-table-sortable runs-col-stats"
              onClick={() => handleSort('success_rate')}
              data-testid="sort-run-stats"
            >
              Summary Stats{renderSortIcon('success_rate')}
            </th>
            <th className="runs-col-actions">Action</th>
          </tr>
        </thead>
        <tbody>
          {sortedRuns.map((run) => (
            <tr
              key={run.id}
              data-testid={`run-row-${run.id}`}
              className="runs-table-clickable-row"
              onClick={() => handleRowClick(run.id)}
            >
              <td data-testid="run-name-cell" className="run-name-cell">
                {run.name}
                {run.refined && <span className="run-refined-badge" title="Refined mode">R</span>}
              </td>
              <td data-testid="run-created-cell" className="run-created-cell">{formatDate(run.created_at)}</td>
              <td data-testid="run-agent-cell" className="run-agent-cell">
                {run.agent_names && run.agent_names.length > 1
                  ? run.agent_names.join(', ')
                  : run.agent_name}
              </td>
              <td data-testid="run-selections-cell" className="run-selections-cell">{formatSelections(run)}</td>
              <td data-testid="run-stats-cell" className="run-stats-cell">{formatStats(run)}</td>
              <td className="run-actions-cell" onClick={(e) => e.stopPropagation()}>
                <DeleteRunConfirmation
                  runId={run.id}
                  onConfirm={() => onDelete(run.id)}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default RunsTable
